import axios from "axios";
import { getUserToken } from "../../hooks/authFunctions";

const baseUrl = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

//init axios
const axiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json"
  }
});

/**
 * Fetch quotes joined with their estimation data, filtered by a single
 * {key, type, value} condition and paginated via DynamoDB's native cursor.
 * @param {{key: string, type: string, value: string|number}|null} filter - The filter condition, or null for no filter
 * @param {number} limit - Page size (max 100)
 * @param {string|null} lastEvaluatedKey - Base64 cursor returned from a previous call's meta, or null for the first page
 * @returns {Promise<Object>} - { data: [{estimation, quote}], meta: { limit, lastEvaluatedKey } }
 */
export const fetchFilteredQuotes = async (
  filter = null,
  limit = 20,
  lastEvaluatedKey = null
) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const params = { limit };
    if (filter) {
      params.filter = encodeURIComponent(JSON.stringify(filter));
    }
    if (lastEvaluatedKey) {
      params.lastEvaluatedKey = lastEvaluatedKey;
    }

    const response = await axiosInstance.get(`/quote-crm/filter`, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered quotes:", error);
    throw error;
  }
};

/**
 * Fetch quote by id
 * @param {string} id - The id of the quote
 * @returns {Promise<Object>} - The quote
 */
export const fetchQuoteById = async (id) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.get(`/quote?quoteId=${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching quote by id:", error);
    throw error;
  }
};

/**
 * Fetch the list of rules-engine versions a super-admin can assign to a quote.
 * Super-admin only - the backend returns version labels only, never paths.
 * @returns {Promise<string[]>} - Available version labels
 */
export const fetchAvailableRulesEngines = async () => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.get(`/quote-crm/rules-engines`);
    return response.data?.versions ?? [];
  } catch (error) {
    console.error("Error fetching available rules engines:", error);
    throw error;
  }
};

/**
 * Super-admin only: override which rules-engine version a quote uses.
 * @param {string} quoteId - The quote id
 * @param {string} version - The version label to assign, or "" for the default engine
 * @returns {Promise<Object>} - { qoute: <updated quote> }
 */
export const updateQuoteRulesEngine = async (quoteId, version) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.put(`/quote-crm/${quoteId}/rules-engine`, { version });
    return response.data;
  } catch (error) {
    console.error("Error updating quote rules engine:", error);
    throw error;
  }
};

/**
 * Update an estimation's representative details
 * @param {string} estimationId - The estimation id
 * @param {{email: string, phone: string, customerrep: string}} representative - The representative details
 * @returns {Promise<Object>} - { success: true }
 */
export const updateEstimationRepresentative = async (estimationId, representative) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.patch(
      `/estimation-crm/${estimationId}/representative`,
      representative
    );
    return response.data;
  } catch (error) {
    console.error("Error updating estimation representative:", error);
    throw error;
  }
};

/**
 * Update an estimation's job number
 * @param {string} estimationId - The estimation id
 * @param {string} jobNumber - The new job number
 * @returns {Promise<Object>} - { success: true }
 */
export const updateEstimationJobNumber = async (estimationId, jobNumber) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.patch(
      `/estimation-crm/${estimationId}/job-number`,
      { jobNumber }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating estimation job number:", error);
    throw error;
  }
};

export const applyDiscount = async (quoteId, code) => {
  try {
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;
    const response = await axiosInstance.put(
      `quote/applyPromoCode?quoteId=${quoteId}`,
      {
        promoCode: code,
      }
    );
    return response.data;
  } catch (err) {
    console.error("Error updating quote contact:", err);
    throw err;
  }
};

export const removeDiscount = async (quoteId, code) => {
  try {
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;
    const response = await axiosInstance.put(
      `quote/applyPromoCode?quoteId=${quoteId}`,
      {
         promoCode: 'Default',
      }
    );
    return response.data;
  } catch (err) {
    console.error("Error updating quote contact:", err);
    throw err;
  }
};

/**
 * purchasePolicy
 * @param {Object} data - The values to update
 * @returns {Promise<Object>} - The quote
 */
export const purchasePolicy = async (data) => {
  try {
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;
    const response = await axiosInstance.post(
      `policy`,
      data
    );
    return response;
  } catch (err) {
    console.error("Error updating quote contact:", err);
    throw err;
  }
};

/**
 * updateQuotePolicy
 * @param {Object} data - The values to update
 * @returns {Promise<Object>} - The quote
 */
export const updateQuotePolicy = async (data) => {
  try {
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;
    const response = await axiosInstance.put(
      `policy/policy-update-request-for-customer`,
      data
    );
    return response;
  } catch (err) {
    console.error("Error updating quote contact:", err);
    throw err;
  }
}

/**
 * Update quote updateContact
 * {BASE_URL}/updateContact?quoteId={QUOTE_ID} put
 * @param {string} id - The id of the quote
 * @param {Object} values - The values to update
 * @returns {Promise<Object>} - The quote
 */
export const updateQuoteContact = async (id, values) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.put(
      `/quote/updateContact?quoteId=${id}`,
      values
    );
    return response.data;
  } catch (error) {
    console.error("Error updating quote contact:", error);
    throw error;
  }
};

/**
 * Delete carrier
 * @param {string} dot - The dot of the carrier
 * @returns {Promise<Object>} - The quote
 */
export const deleteCarrier = async (dot) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIERS_BASE_URL;

    const response = await axiosInstance.delete(`/carrier?dot=${dot}`);
    return { ...response.data, status: response.status };
  } catch (error) {
    console.error("Error updating quote contact:", error);
    throw error;
  }
};

/**
 * Fetch policies
 * @param {number} page - The page number
 * @param {number} limit - The limit of the policies
 * @param {string} token - The token of the user
 * @returns {Promise<Object>} - The policies
 */
export const fetchPolicies = async (
  page = 1,
  limit = 20,
  sortBy = "policyDate",
  sortDirection = "asc",
  filter = '{"quoteId":""}',
  searchTerm = ""
) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.get(`/policy-crm/policies`, {
      params: { page, limit, sortBy, sortDirection, filter, searchTerm }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching policies:", error);
    throw error;
  }
};

/**
 * Add policy
 * @param {Object} policyObj - The policy object
 * @returns {Promise<Object>} - The policy
 */
export const addPolicy = async (policyObj) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.post("/policy", policyObj);
    return response.data;
  } catch (error) {
    console.error("Error adding policy:", error);
    throw error;
  }
};

/**
 * Fetch policy by id
 * @param {string} id - The id of the policy
 * @returns {Promise<Object>} - The policy
 */
export const fetchPolicyById = async (id) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    //fetch policy by id
    const response = await axiosInstance.get(`/policy?policyId=${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching policy by id:", error);
    throw error;
  }
};

/**
 * handleRequestUpdatePolicy
 * @param {string} policyId - The id of the policy
 * @returns {Promise<Object>} - The policy
 */
export const handleRequestUpdatePolicy = async (policyId) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.put(
      `/policy/start-policy-update-for-customer?policyId=${policyId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error handling request update policy:", error);
    throw error;
  }
};

/**
 * handleRequestUpdatePolicy
 * @param {string} policyId - The id of the policy
 * @returns {Promise<Object>} - The policy
 */
export const handleQuotePolicyUpdateRequest = async (quoteId) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.put(
      `/policy/policy-update-request-for-customer`,
      { quoteId }
    );
    return response.data;
  } catch (error) {
    console.error("Error handling request update policy:", error);
    throw error;
  }
};

/**
 * Send a manual estimation to the parse/create Lambda (same endpoint used by carriers-fe).
 * @param {FormData|Object} payload - FormData (file mode) or a plain object (text/create mode), containing a `mode` field of 'carrier-parse' or 'create'
 * @returns {Promise<Object>} - For 'carrier-parse': { estimation, s3Key?, errors? }. For 'create': the created estimation.
 */
export const sendManualEstimation = async (payload) => {
  try {
    const user = await getUserToken();
    const headers = {};
    if (user.idToken.toString()) {
      headers["Authorization"] = `Bearer ${user.idToken.toString()}`;
    }

    const isFormData = payload instanceof FormData;
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    const response = await axios.post(
      process.env.REACT_APP_MANUAL_UPLOAD_API,
      payload,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error sending manual estimation:", error);
    throw error;
  }
};

/**
 * Fetch carriers
 * https://dev-carriers-c1c90faa.umisusa.com/carrier/carriers?page=2&limit=5
 * @param {number} page - The page number
 * @param {number} limit - The limit of the carriers
 * @returns {Promise<Object>} - The carriers
 */
export const fetchCarriers = async (page = 1, limit = 20, searchTerm) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIERS_BASE_URL;

    const response = await axiosInstance.get("/carrier/carriers", {
      params: { page, limit, searchTerm }
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching carriers:", error);
    throw error;
  }
};

/**
 * Add carrier
 * @param {Object} carrierObj - The carrier object
 * @param {string} editUrl - The edit url
 * @returns {Promise<Object>} - The carrier
 */
export const addCarrier = async (carrierObj, editUrl = "") => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIERS_BASE_URL;

    //check if editUrl is present
    if (editUrl) {
      //use put
      const response = await axiosInstance.put(
        `/carrier${editUrl}`,
        carrierObj
      );
      //return response
      return response.data;
    }

    //for new carrier
    const response = await axiosInstance.post("/carrier", carrierObj);
    return response.data;
  } catch (error) {
    console.error("Error adding carrier:", error);
    throw error;
  }
};

/**
 * Add a licensed state to a carrier
 * @param {string} dot - The carrier's DOT
 * @param {string} state - Short state name, e.g. "CA"
 */
export const addCarrierLicensedState = async (dot, state) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIERS_BASE_URL;

    const response = await axiosInstance.put(
      `/carrier/addLocalSupportedState?dot=${encodeURIComponent(dot)}`,
      { state }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding carrier licensed state:", error);
    throw error;
  }
};

/**
 * Remove a licensed state from a carrier
 * @param {string} dot - The carrier's DOT
 * @param {string} state - Short state name, e.g. "CA"
 */
export const removeCarrierLicensedState = async (dot, state) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIERS_BASE_URL;

    const response = await axiosInstance.put(
      `/carrier/removeLocalSupportedState?dot=${encodeURIComponent(dot)}`,
      { state }
    );
    return response.data;
  } catch (error) {
    console.error("Error removing carrier licensed state:", error);
    throw error;
  }
};

/**
 * Fetch carrier by id
 * @param {string} id - The id of the carrier
 * @returns {Promise<Object>} - The carrier
 */
export const fetchCarrierById = async (id) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIERS_BASE_URL;

    const response = await axiosInstance.get(`/carrier?dot=${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching carrier by id:", error);
    throw error;
  }
};

/**
 * Cancel policy
 * @param {string} policyId - The id of the policy
 * @returns {Promise<Object>} - The policy
 */
export const cancelPolicy = async (policyId, cancelationSupport) => {
  try {
    // /policy/cancel?policyId=${policy?.props?.policyId}` DELETE header
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.put(
      `/policy/cancel-policy-for-customer?policyId=${policyId}&cancelationSupport=${cancelationSupport}`
    );
    return response.data;
  } catch (error) {
    console.error("Error canceling policy:", error);
    throw error;
  }
};

/**
 * Update moving data
 * @param {Object} values - The values to update
 * @param {string} quoteId - The id of the quote
 * @returns {Promise<Object>} - The moving data
 */
export const updateMovingData = async (values, quoteId) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.put(
      `/quote/updateMovingData?quoteId=${quoteId}`,
      values
    );
    return response.data;
  } catch (error) {
    console.error("Error updating moving data:", error);
    throw error;
  }
};

/**
 * Add inventory item
 * @param {Object} itemObj - The item object
 * @param {string} quoteId - The id of the quote
 * @param {string} mode - The mode of the item
 * @returns {Promise<Object>} - The item
 */
export const addInventoryItem = async (itemObj, quoteId, mode = "new") => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.put(
      `/quote/${mode === "new" ? "addNewItems" : "updateExsistingItems"
      }?quoteId=${quoteId}`,
      [itemObj]
    );
    return response.data;
  } catch (error) {
    console.error("Error adding inventory item:", error);
    throw error;
  }
};

/**
 * Delete inventory item
 * @param {string} quoteId - The id of the quote
 * @param {string} itemId - The id of the item
 * @returns {Promise<Object>} - The item
 */
export const deleteInventoryItem = async (quoteId, itemId) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.delete(
      `/quote/deleteQuoteItems?quoteId=${quoteId}&itemId=${itemId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting inventory item:", error);
    throw error;
  }
};

/**
 * Fetch quotes for policy
 * @param {string} policyId - The id of the policy
 * @returns {Promise<Object>} - The quotes
 */
export const fetchQuotesForPolicy = async (policyId) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.get(
      `/policy-crm/quotesForPolicy?policyId=${policyId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching quotes for policy:", error);
    throw error;
  }
};

export const getFileClaimStatus = async (policyId) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.get(
       `/policy/checkIfValidForClaim?policyId=${policyId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching quotes for policy:", error);
    throw error;
  }
};

/**
 * Set executing carrier for a policy
 * @param {string} policyId - The id of the policy
 * @param {string} carrierDOT - The DOT number of the carrier
 * @returns {Promise<Object>} - The updated policy
 */
export const setExecutingCarrier = async (policyId, carrierDOT) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;
    const response = await axiosInstance.put(
      `/policy/set-executing-carrier?policyId=${policyId}&carrierDOT=${carrierDOT}`
    );
    return response.data;
  } catch (error) {
    console.error("Error setting executing carrier:", error);
    throw error;
  }
};

export const updatePolicyDropoffDate = async (policyId, dropoffDate) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;
    const response = await axiosInstance.put(
      `/policy/update-policy-dropoff-date?policyId=${policyId}&dropoffDate=${encodeURIComponent(dropoffDate)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error updating policy dropoff date:", error);
    throw error;
  }
};

export const getClaimDetails = async (policyNumber) => {
  try {
    //get user
    const user = await getUserToken();
    //check if token is provided
    if (user.idToken.toString()) {
      //add token to axios instance
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }

    //change base url
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CLAIMS_DETAILS_API;

    const response = await axiosInstance.get(
       `/${policyNumber}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching quotes for policy:", error);
    throw error;
  }
};

/**
 * Fetch all carrier agents with their assigned DOTs and roles (admin only)
 * @returns {Promise<Array>} - The agents, each with { email, name, phone, status, carrierDOTs, roles }
 */
export const fetchAgentsForAdmin = async () => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.get("/umis-agents");
    return response.data;
  } catch (error) {
    console.error("Error fetching agents:", error);
    throw error;
  }
};

/**
 * Add a DOT to an agent
 * @param {string} email - The agent's email
 * @param {string} dot - The DOT to add
 */
export const addAgentDot = async (email, dot) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.post(
      `/umis-agents/${encodeURIComponent(email)}/dots`,
      { dot }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding agent DOT:", error);
    throw error;
  }
};

/**
 * Remove a DOT from an agent
 * @param {string} email - The agent's email
 * @param {string} dot - The DOT to remove
 */
export const removeAgentDot = async (email, dot) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.delete(
      `/umis-agents/${encodeURIComponent(email)}/dots/${encodeURIComponent(dot)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error removing agent DOT:", error);
    throw error;
  }
};

/**
 * Add a role to an agent
 * @param {string} email - The agent's email
 * @param {string} role - The role to add
 */
export const addAgentRole = async (email, role) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.post(
      `/umis-agents/${encodeURIComponent(email)}/roles`,
      { role }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding agent role:", error);
    throw error;
  }
};

/**
 * Remove a role from an agent
 * @param {string} email - The agent's email
 * @param {string} role - The role to remove
 */
export const removeAgentRole = async (email, role) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.delete(
      `/umis-agents/${encodeURIComponent(email)}/roles/${encodeURIComponent(role)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error removing agent role:", error);
    throw error;
  }
};

/**
 * Soft-delete a quote (sets status to DELETED).
 * @param {string} quoteId - The id of the quote
 */
export const deleteQuote = async (quoteId) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_ESTIMATIONS_BASE_URL;

    const response = await axiosInstance.delete(`/quote-crm/${encodeURIComponent(quoteId)}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting quote:", error);
    throw error;
  }
};

/**
 * Delete (soft-delete) an agent — marks the agent record DELETED and strips their
 * Cognito DOT/role groups.
 * @param {string} email - The agent's email
 */
export const deleteAgent = async (email) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.delete(
      `/umis-agents/${encodeURIComponent(email)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting agent:", error);
    throw error;
  }
};

/**
 * List the named templates that exist within a scope (for the "pick or create a template"
 * step before editing one).
 * @param {string|null} dot - A carrier DOT, or null/omitted for the general/fallback scope.
 * @returns {Promise<Array<{templateId: string, name: string, createdAt: string, createdBy: string}>>}
 */
export const listEmailTemplatesForScope = async (dot) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.get("/umis-email-templates/list", {
      params: dot ? { dot } : {}
    });
    return response.data;
  } catch (error) {
    console.error("Error listing email templates:", error);
    throw error;
  }
};

/**
 * Create a new named, empty template within a scope.
 * @param {string|null} dot - A carrier DOT, or null/omitted for the general scope.
 * @param {string} name
 * @returns {Promise<{templateId: string, name: string}>}
 */
export const createEmailTemplate = async (dot, name) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.post("/umis-email-templates/create", {
      dot: dot || undefined,
      name
    });
    return response.data;
  } catch (error) {
    console.error("Error creating email template:", error);
    throw error;
  }
};

/**
 * Fetch the active version + full version history for one named template.
 * @param {string|null} dot - A carrier DOT, or null/omitted for the general/fallback scope.
 * @param {string} templateId
 * @returns {Promise<{active: Object|null, versions: Array, logoPreviewUrl: string|null}>}
 */
export const getEmailTemplateAdmin = async (dot, templateId) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.get("/umis-email-templates", {
      params: { ...(dot ? { dot } : {}), templateId }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching email template:", error);
    throw error;
  }
};

/**
 * Save a new draft version of a named template. Does not make it active.
 * @param {string|null} dot - A carrier DOT, or null/omitted for the general scope.
 * @param {string} templateId
 * @param {string} subject
 * @param {string} templateHtml
 * @returns {Promise<{version: number, variables: string[]}>}
 */
export const saveEmailTemplateAdmin = async (dot, templateId, subject, templateHtml) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.put("/umis-email-templates", {
      dot: dot || undefined,
      templateId,
      subject,
      templateHtml
    });
    return response.data;
  } catch (error) {
    console.error("Error saving email template:", error);
    throw error;
  }
};

/**
 * Activate a previously-saved draft version, making it the one agents actually send.
 * @param {string|null} dot - A carrier DOT, or null/omitted for the general scope.
 * @param {string} templateId
 * @param {number} version
 * @returns {Promise<{success: boolean}>}
 */
export const activateEmailTemplateVersion = async (dot, templateId, version) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.post("/umis-email-templates/activate", {
      dot: dot || undefined,
      templateId,
      version
    });
    return response.data;
  } catch (error) {
    console.error("Error activating email template version:", error);
    throw error;
  }
};

/**
 * Upload/replace the logo for an email template scope.
 * @param {string|null} dot - A carrier DOT, or null/omitted for the general template.
 * @param {File} file
 * @returns {Promise<{logoS3Key: string}>}
 */
export const uploadEmailTemplateLogoAdmin = async (dot, file) => {
  try {
    const user = await getUserToken();
    const headers = {};
    if (user.idToken.toString()) {
      headers["Authorization"] = `Bearer ${user.idToken.toString()}`;
    }

    const fd = new FormData();
    fd.append("logo", file);

    const response = await axios.post(
      `${process.env.REACT_APP_CARRIER_AGENTS_BASE_URL}/umis-email-templates/logo${dot ? `?dot=${encodeURIComponent(dot)}` : ""}`,
      fd,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading email template logo:", error);
    throw error;
  }
};

/**
 * List the named contract templates that exist within a scope (for the "pick or create a
 * template" step before editing one).
 * @param {string|null} dot - A carrier DOT, or null/omitted for the general/fallback scope.
 * @returns {Promise<Array<{templateId: string, name: string, variant: string|null, createdAt: string, createdBy: string}>>}
 */
export const listContractTemplatesForScope = async (dot) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.get("/umis-contract-templates/list", {
      params: dot ? { dot } : {}
    });
    return response.data;
  } catch (error) {
    console.error("Error listing contract templates:", error);
    throw error;
  }
};

/**
 * Create a new named, empty contract template within a scope.
 * @param {string|null} dot - A carrier DOT, or null/omitted for the general scope.
 * @param {string} name
 * @param {string|null} variant - 'GRANOT' | 'OTHER', required for the general scope only.
 * @returns {Promise<{templateId: string, name: string, variant: string|null}>}
 */
export const createContractTemplate = async (dot, name, variant) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.post("/umis-contract-templates/create", {
      dot: dot || undefined,
      name,
      variant: variant || undefined
    });
    return response.data;
  } catch (error) {
    console.error("Error creating contract template:", error);
    throw error;
  }
};

/**
 * Fetch the active version + full version history for one named contract template.
 * @param {string|null} dot - A carrier DOT, or null/omitted for the general/fallback scope.
 * @param {string} templateId
 * @returns {Promise<{active: Object|null, versions: Array}>}
 */
export const getContractTemplateAdmin = async (dot, templateId) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.get("/umis-contract-templates", {
      params: { ...(dot ? { dot } : {}), templateId }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching contract template:", error);
    throw error;
  }
};

/**
 * Save a new draft version of a named contract template. Does not make it active.
 * @param {string|null} dot - A carrier DOT, or null/omitted for the general scope.
 * @param {string} templateId
 * @param {string} templateHtml
 * @returns {Promise<{version: number, variables: string[]}>}
 */
export const saveContractTemplateAdmin = async (dot, templateId, templateHtml) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.put("/umis-contract-templates", {
      dot: dot || undefined,
      templateId,
      templateHtml
    });
    return response.data;
  } catch (error) {
    console.error("Error saving contract template:", error);
    throw error;
  }
};

/**
 * Activate a previously-saved draft version, making it the one the onboarding flow resolves to.
 * @param {string|null} dot - A carrier DOT, or null/omitted for the general scope.
 * @param {string} templateId
 * @param {number} version
 * @returns {Promise<{success: boolean}>}
 */
export const activateContractTemplateVersion = async (dot, templateId, version) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.post("/umis-contract-templates/activate", {
      dot: dot || undefined,
      templateId,
      version
    });
    return response.data;
  } catch (error) {
    console.error("Error activating contract template version:", error);
    throw error;
  }
};

/**
 * Look up a DOT's registry details (legal name, addresses, MC number, status) ahead of
 * pre-provisioning it — same lookup CarrierOnboarding.tsx's own 'start' step uses, just
 * reachable here via the admin pool.
 * @param {string} dot
 * @returns {Promise<{companyDetails: Object|null, carrierExists: boolean}>}
 */
export const lookupContractTemplateDot = async (dot) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.get("/umis-contract-templates/dot-lookup", {
      params: { dot }
    });
    return response.data;
  } catch (error) {
    console.error("Error looking up DOT:", error);
    throw error;
  }
};

/**
 * Pre-provision a carrier record (status NEW) for a DOT that hasn't started onboarding yet,
 * purely so a custom contract template can be set up for it ahead of time.
 * @param {string} dot
 * @param {Object} companyDetails
 * @returns {Promise<Object>}
 */
export const addContractTemplateDot = async (dot, companyDetails) => {
  try {
    const user = await getUserToken();
    if (user.idToken.toString()) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${user.idToken.toString()}`;
    }
    axiosInstance.defaults.baseURL = process.env.REACT_APP_CARRIER_AGENTS_BASE_URL;

    const response = await axiosInstance.post("/umis-contract-templates/add-dot", {
      dot,
      companyDetails
    });
    return response.data;
  } catch (error) {
    console.error("Error adding new DOT:", error);
    throw error;
  }
};
